import lodashSet from "lodash/set";
import * as Yup from "yup";
import { type PillarFormInputComponentConfig } from "shared/components/pillar-form/PillarFormInputComponentConfig";

/**
 * A nested mirror of the form's values: element names are dotted paths
 * (`invokeParams.certificationEvent`), and the values behind them are written
 * with the same lodash `set`, so the validation tree lands on exactly the paths
 * the modals submit.
 */
type DynamicFormSchemaTree = {
  [key: string]: Yup.AnySchema | DynamicFormSchemaTree;
};

/**
 * One test covers every element type the dynamic renderer produces: a text or
 * date input leaves `""`, a select that was never touched leaves `undefined`,
 * a cleared one leaves `null`, and a multi select leaves `[]`.
 */
const buildRequiredSchema = (label: string): Yup.AnySchema =>
  Yup.mixed().test(
    "dynamic-required",
    `${label} is required`,
    (value) =>
      value !== undefined &&
      value !== null &&
      value !== "" &&
      (!Array.isArray(value) || value.length > 0),
  );

const toObjectSchema = (tree: DynamicFormSchemaTree): Yup.AnyObjectSchema =>
  Yup.object(
    Object.fromEntries(
      Object.entries(tree).map(([key, node]) => [
        key,
        Yup.isSchema(node)
          ? (node as Yup.AnySchema)
          : toObjectSchema(node as DynamicFormSchemaTree),
      ]),
    ),
  );

/**
 * Turns the `isRequired` flags on a surveyjs-like element list into a Yup
 * schema, folded into whatever rules the form owns itself.
 *
 * Definitions author `isRequired` the way surveyjs does, so a workflow can say
 * a field has to be filled in without the modal knowing anything about it.
 * Nothing is required unless a definition asks for it, so existing definitions
 * validate exactly as they did before.
 */
export const buildDynamicFormValidationSchema = (
  elements: PillarFormInputComponentConfig[],
  baseShape: Record<string, Yup.AnySchema> = {},
): Yup.AnyObjectSchema => {
  const tree: DynamicFormSchemaTree = { ...baseShape };

  elements.forEach((element) => {
    if (!element.isRequired) {
      return;
    }

    lodashSet(
      tree,
      element.name,
      buildRequiredSchema(element.title ?? element.name),
    );
  });

  return toObjectSchema(tree);
};
